// src/components/DashPavimentos.jsx
import React, { useState, useEffect } from "react";
import "./DashPavimentos.css";

const extrairTexto = (item) => {
  if (!item) return "";
  return typeof item === "string" ? item : (item.name ?? item.nome ?? "");
};

const ehTerreo = (nome) => {
  const n = nome.toLowerCase().trim();
  return (
    n === "térreo" ||
    n === "terreo" ||
    n === "pavimento térreo" ||
    n === "pavimento terreo" ||
    n === "pav. térreo" ||
    n === "pav. terreo" ||
    n === "pav térreo" ||
    n === "pav terreo"
  );
};

// Função para formatar data corretamente sem problemas de fuso horário
const formatarDataCorreta = (data) => {
  if (!data) return "";
  
  // Se for string, converte para Date
  const dateObj = typeof data === 'string' ? new Date(data) : data;
  
  // Verifica se é uma data válida
  if (isNaN(dateObj.getTime())) return data;
  
  // Formata a data mantendo o dia/mês/ano sem alteração de fuso
  const dia = String(dateObj.getDate()).padStart(2, '0');
  const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
  const ano = dateObj.getFullYear();
  
  return `${dia}/${mes}/${ano}`;
};

const PAV_H = 8;
const PAV_GAP = 2;
const STEP = PAV_H + PAV_GAP;
const LOCACAO_SIZE = 14;
const LOCACAO_GAP = 6;

const calcularDiasAtras = (data) => {
  if (!data) return Infinity;

  let dataAtiv;
  if (typeof data === 'string') {
    const soData = data.split('T')[0]; // remove hora, se tiver
    const [ano, mes, dia] = soData.split('-').map(Number);
    if (!ano || !mes || !dia) return Infinity;
    dataAtiv = new Date(ano, mes - 1, dia); // cria em horário LOCAL, sem UTC
  } else {
    dataAtiv = data;
  }

  if (isNaN(dataAtiv.getTime())) return Infinity;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const dataComparar = new Date(dataAtiv);
  dataComparar.setHours(0, 0, 0, 0);

  const diffMs = hoje - dataComparar;
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

const getColor = (pav, pavStats, pavimentosComAtividade) => {
  if (!pav?.id) return "#cbd5e1";
  const nome = extrairTexto(pav);

  // pavimentosComAtividade é um Map: nome -> data da última atividade
  const dataAtividade = pavimentosComAtividade?.get?.(nome);
  if (dataAtividade) {
    const diasAtras = calcularDiasAtras(dataAtividade);
    if (diasAtras <= 1) return "#86efac"; // verde claro - hoje ou ontem
    return "#3b82f6"; // azul - 2+ dias
  }

  return "#cbd5e1"; // cinza - sem atividade registrada
};

function BuildingDiagram({ pavimentos, locacoes, pavStats, pavimentosComAtividade, onPavClick, onLocacaoClick }) {
  // Inverter a ordem dos pavimentos para o desenho
  const pav = [...pavimentos].reverse();
  const locs = locacoes || [];

  const terreoIdx = pav.findIndex((p) => ehTerreo(extrairTexto(p)));

  const TELHADO_TOP = 4;
  const TELHADO_H = 24;
  const Y_START = TELHADO_TOP + TELHADO_H + 4;
  const totalPav = pav.length;
  
  // Espaço para as locações (abaixo da torre)
  const LOCACOES_Y_START = Y_START + totalPav * STEP + 20;
  const totalLocs = locs.length;
  const locsPorLinha = Math.min(4, totalLocs);
  const linhasLoc = Math.ceil(totalLocs / locsPorLinha);
  const LOCACOES_H = linhasLoc > 0 ? linhasLoc * (LOCACAO_SIZE + LOCACAO_GAP) + 10 : 0;
  
  const svgH = LOCACOES_Y_START + LOCACOES_H + 20;

  return (
    <svg
      viewBox={`0 0 130 ${svgH}`}
      xmlns="http://www.w3.org/2000/svg"
      className="dash-pav-building-svg"
    >
      {/* Telhado */}
      <polygon
        points={`65,${TELHADO_TOP} 118,${TELHADO_TOP + TELHADO_H} 12,${TELHADO_TOP + TELHADO_H}`}
        fill="#94a3b8"
      />
      <rect
        x="10"
        y={TELHADO_TOP + TELHADO_H}
        width="110"
        height="6"
        rx="1"
        fill="#cbd5e1"
      />

      {/* Pavimentos (Torre) */}
      {pav.map((p, i) => {
        const y = Y_START + i * STEP;
        const isT = terreoIdx !== -1 && i === terreoIdx;
        const color = isT
          ? getColor(p, pavStats, pavimentosComAtividade) === "#cbd5e1"
            ? "#a7f3d0"
            : "#22c55e"
          : getColor(p, pavStats, pavimentosComAtividade);
        const nome = extrairTexto(p);

        return (
          <React.Fragment key={p?.id ?? i}>
            <rect
              x="10"
              y={y}
              width="110"
              height={PAV_H}
              rx="1"
              fill={color}
              opacity={isT ? 0.95 : 0.85}
              stroke={isT ? "#020a52" : "none"}
              strokeWidth={isT ? 1.5 : 0}
              className="dash-pav-bar"
              onClick={() => onPavClick && onPavClick(p)}
            >
              <title>{nome}</title>
            </rect>
            {isT && (
              <line
                x1="0"
                y1={y + PAV_H}
                x2="130"
                y2={y + PAV_H}
                stroke="#020a52"
                strokeWidth="2.5"
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Fundação da torre */}
      <rect
        x="4"
        y={Y_START + totalPav * STEP + 2}
        width="122"
        height="8"
        rx="1"
        fill="#94a3b8"
        opacity="0.6"
      />

      {/* Separador */}
      <line
        x1="10"
        y1={LOCACOES_Y_START - 6}
        x2="120"
        y2={LOCACOES_Y_START - 6}
        stroke="#d0d8e8"
        strokeWidth="1"
        strokeDasharray="4,4"
        opacity="0.6"
      />

      {/* Título das Locações */}
      {totalLocs > 0 && (
        <text
          x="65"
          y={LOCACOES_Y_START + 2}
          textAnchor="middle"
          fontSize="6"
          fill="#8896b3"
          fontWeight="600"
          letterSpacing="0.5"
        >
          Locações
        </text>
      )}

      {/* Locações (quadrados abaixo) */}
      {locs.map((loc, i) => {
        const col = i % locsPorLinha;
        const row = Math.floor(i / locsPorLinha);
        const x = 15 + col * (LOCACAO_SIZE + LOCACAO_GAP + 8);
        const y = LOCACOES_Y_START + 10 + row * (LOCACAO_SIZE + LOCACAO_GAP);
        const nome = extrairTexto(loc);
        const color = getColor(loc, pavStats, pavimentosComAtividade);

        return (
          <React.Fragment key={loc?.id ?? i}>
            <rect
              x={x}
              y={y}
              width={LOCACAO_SIZE}
              height={LOCACAO_SIZE}
              rx="2"
              fill={color}
              opacity="0.85"
              stroke="#020a52"
              strokeWidth="0.8"
              className="dash-pav-bar"
              onClick={() => onLocacaoClick && onLocacaoClick(loc)}
            >
              <title>{nome}</title>
            </rect>
            <text
              x={x + LOCACAO_SIZE / 2}
              y={y + LOCACAO_SIZE + 10}
              textAnchor="middle"
              fontSize="5"
              fill="#6c7a91"
              fontWeight="500"
              style={{ pointerEvents: "none" }}
            >
              {nome.length > 10 ? nome.substring(0, 8) + "…" : nome}
            </text>
          </React.Fragment>
        );
      })}
    </svg>
  );
}

function DashPavimentos({ pavimentos, locacoes = [], pavStats, pavimentosComAtividade, onVerRegistros }) {
  const [pavModalAberto, setPavModalAberto] = useState(null);
  const [pavRdos, setPavRdos] = useState([]);
  const [pavRdosLoading, setPavRdosLoading] = useState(false);
  const [infoAberto, setInfoAberto] = useState(false);

  // Fecha o popover ao clicar fora
  useEffect(() => {
    if (!infoAberto) return;
    const fechar = () => setInfoAberto(false);
    document.addEventListener("click", fechar);
    return () => document.removeEventListener("click", fechar);
  }, [infoAberto]);

  const abrirPavModal = async (pavNome, onFetchRdos) => {
    setPavModalAberto(pavNome);
    setPavRdos([]);
    setPavRdosLoading(true);
    if (onFetchRdos) {
      const rdos = await onFetchRdos(pavNome);
      // Corrige as datas dos RDOs para evitar problema de fuso horário
      const rdosComDataCorrigida = rdos.map(r => ({
        ...r,
        data: formatarDataCorreta(r.data)
      }));
      setPavRdos(rdosComDataCorrigida);
    }
    setPavRdosLoading(false);
  };

  const fecharModal = () => {
    setPavModalAberto(null);
    setPavRdos([]);
  };

  const handleClickPav = (pav) => {
    if (onVerRegistros) {
      onVerRegistros(pav, abrirPavModal);
    } else {
      abrirPavModal(extrairTexto(pav), null);
    }
  };

  const handleClickLocacao = (loc) => {
    if (onVerRegistros) {
      onVerRegistros(loc, abrirPavModal);
    } else {
      abrirPavModal(extrairTexto(loc), null);
    }
  };

  // Filtra apenas os pavimentos (tipo "pavimento" ou sem tipo)
  const pavimentosFiltrados = pavimentos.filter(p => !p?.tipo || p.tipo === "pavimento");
  
  // Filtra apenas as locações (tipo "locacao")
  const locacoesFiltradas = locacoes.filter(p => p?.tipo === "locacao");

  return (
    <>
      <div className="dash-pav">
        <div className="dash-pav-header">
          <span className="dash-pav-title">PAVIMENTOS DA OBRA</span>
          <div className="dash-pav-info-wrap">
            <button
              type="button"
              className="dash-pav-info-btn"
              onClick={(e) => {
                e.stopPropagation();
                setInfoAberto((v) => !v);
              }}
              aria-label="Como funciona"
            >
              ?
            </button>
            {infoAberto && (
              <div className="dash-pav-info-popover" onClick={(e) => e.stopPropagation()}>
                <div className="dash-pav-info-title">Como funciona</div>
                <p className="dash-pav-info-text">
                  A torre é representada nos blocos de pavimentos e as locações administrativas são representadas nos quadrados abaixo.
                  O prédio e a tabela mostram o status de cada pavimento/locação com base nas atividades registradas nos Diários de Obra (RDOs) e nas tarefas do Kanban.
                </p>
               <div className="dash-pav-info-legenda">
                  <div className="dash-pav-info-item">
                    <span className="dash-pav-info-dot" style={{ background: "#86efac" }} />
                    Atividade registrada hoje ou ontem
                  </div>
                  <div className="dash-pav-info-item">
                    <span className="dash-pav-info-dot" style={{ background: "#3b82f6" }} />
                    Atividade registrada há 2 dias ou mais
                  </div>
                  <div className="dash-pav-info-item">
                    <span className="dash-pav-info-dot" style={{ background: "#cbd5e1" }} />
                    Sem atividade registrada
                  </div>
                </div>
                <p className="dash-pav-info-text">
                  Clique em um pavimento ou locação (no prédio ou na tabela) para ver o histórico de atividades registradas nos RDOs.
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="dash-pav-grid">
          <div className="dash-pav-building-wrap">
            <BuildingDiagram 
              pavimentos={pavimentosFiltrados}
              locacoes={locacoesFiltradas}
              pavStats={pavStats}
              pavimentosComAtividade={pavimentosComAtividade}
              onPavClick={handleClickPav}
              onLocacaoClick={handleClickLocacao}
            />
          </div>
          <div className="dash-pav-table-wrap">
            <table className="dash-pav-table">
              <thead>
                <tr>
                  <th>Pavimento / Locação</th>
                  <th>Atividades (RDOs)</th>
                </tr>
              </thead>
              <tbody>
                {/* Pavimentos */}
                {pavimentosFiltrados.map((p, i) => (
                  <tr
                    key={p?.id ?? `pav-${i}`}
                    className={`dash-pav-row${
                      ehTerreo(extrairTexto(p)) ? " dash-pav-row-terreo" : ""
                    }`}
                    onClick={() => handleClickPav(p)}
                  >
                    <td className="dash-pav-nome">
                      {ehTerreo(extrairTexto(p)) && (
                        <span className="dash-pav-terreo-marker"></span>
                      )}
                      {extrairTexto(p)}
                    </td>
                    <td className="dash-pav-acao">Ver registros</td>
                  </tr>
                ))}
                
                {/* Locações */}
                {locacoesFiltradas.map((l, i) => (
                  <tr
                    key={l?.id ?? `loc-${i}`}
                    className="dash-pav-row dash-pav-row-locacao"
                    onClick={() => handleClickLocacao(l)}
                  >
                    <td className="dash-pav-nome">
                      <span className="dash-pav-locacao-marker">📍</span>
                      {extrairTexto(l)}
                    </td>
                    <td className="dash-pav-acao">Ver registros →</td>
                  </tr>
                ))}
                
                {pavimentos.length === 0 && (
                  <tr>
                    <td colSpan={2} className="dash-pav-empty">
                      Sem pavimentos ou locações
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {pavModalAberto && (
        <div className="dash-pav-modal-overlay" onClick={fecharModal}>
          <div
            className="dash-pav-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dash-pav-modal-header">
              <span className="dash-pav-modal-title">{pavModalAberto}</span>
              <button
                type="button"
                className="dash-pav-modal-close"
                onClick={fecharModal}
              >
                ×
              </button>
            </div>
            <div className="dash-pav-modal-body">
              {pavRdosLoading ? (
                <p className="dash-pav-modal-loading">Carregando…</p>
              ) : pavRdos.length === 0 ? (
                <p className="dash-pav-modal-empty">
                  Nenhuma atividade registrada nos Diários de Obra.
                </p>
              ) : (
                pavRdos.map((r, i) => (
                  <div key={i} className="dash-pav-modal-item">
                    <div className="dash-pav-modal-item-date">{r.data}</div>
                    <div className="dash-pav-modal-item-desc">{r.desc}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DashPavimentos;